import { useEffect, useState } from "react";
// import HomepageCollectionSkeleton from "../HomepageCollectionSkeleton";
import getTrendingProperties from "@Pages/Home/helpers/TrendingProperties";
import getNominatedSearchProperties from "@Pages/Home/helpers/NominatedSearchProperties";
import wrapperHOC from "@Utils/wrapperHOC";
import getUserCountry from "@Pages/Search/components/SearchList/components/TrustPilotBanner/helper";
import countryCallingCodes from "constants/countryCallingCodes";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import {
  AU_CITY_CANONICAL_NAMES,
  CA_CITY_CANONICAL_NAMES,
  FR_CITY_CANONICAL_NAMES,
  GER_CITY_CANONICAL_NAMES,
  IR_CITY_CANONICAL_NAMES,
  NZ_CITY_CANONICAL_NAMES,
  SG_CITY_CANONICAL_NAMES,
  SP_CITY_CANONICAL_NAMES,
  UK_CITY_CANONICAL_NAMES,
  US_CITY_CANONICAL_NAMES,
} from "./utils";
import ThousandPropertiesNewRails from "./ThousandPropertiesNewRails";
import { FLAG_BASE_URL } from "../PopularCities/data/popularCountries";
import config from "@Config/index";
//
import useTranslationHook from "hooks/useTranslationHook";

const CITY_CANONICAL_NAMES = [
  { name: "London", name_zh: "伦敦", canonicalName: "london-1811028205760" },
  { name: "Birmingham", name_zh: "伯明翰", canonicalName: "birmingham-1811028953945" },
  { name: "Edinburgh", name_zh: "爱丁堡", canonicalName: "edinburgh-1811029850773" },
  { name: "Bangor", name_zh: "班戈", canonicalName: "bangor-1811020273860" },
  { name: "Manchester", name_zh: "曼彻斯特", canonicalName: "manchester-1811021510595" },
  { name: "Nottingham", name_zh: "诺丁汉", canonicalName: "nottingham-1811025104223" },
  { name: "Leeds", name_zh: "利兹", canonicalName: "leeds-1811025399073" },
  { name: "Coventry", name_zh: "考文垂", canonicalName: "coventry-1811022688464" },
  { name: "Bristol", name_zh: "布里斯托", canonicalName: "bristol-1811028541951" },
  {
    name: "Newcastle Upon Tyne",
    name_zh: "泰恩河畔纽卡斯尔",
    canonicalName: "newcastle-upon-tyne-1811022588361",
  },
  { name: "Cardiff", name_zh: "加的夫", canonicalName: "cardiff-1811021315133" },
  { name: "Plymouth", name_zh: "普利茅斯", canonicalName: "plymouth-1811024617468" },
  { name: "Chester", name_zh: "切斯特", canonicalName: "chester-1811022260382" },
  {
    name: "Newcastle-under-Lyme",
    name_zh: "莱姆河畔纽卡斯尔",
    canonicalName: "newcastle-under-lyme-1811228377459",
  },
  { name: "Guildford", name_zh: "吉尔福德", canonicalName: "guildford-1811021238331" },
  { name: "Belfast", name_zh: "贝尔法斯特", canonicalName: "belfast-1811021897138" },
];

// `short_name` is the label the country pill and its dropdown render **on mweb only** — desktop
// has the room for the full name. The values match the `shortName` field the Popular Cities
// section uses (`PopularCities/data/popularCountries.ts`) so the two rows agree.
//
// The keys stay the full names because they are the identity of a selection (`activeCountry`),
// not display text. Chinese keeps `name_zh`, which is already short.
const COUNTRY_CITY_MAPPING = {
  "United Kingdom": {
    icon: `${FLAG_BASE_URL}flag-uk.svg`,
    sq_icon: `${config.IMAGE_STATIC_URL}/images/sq-flag-uk.svg`,
    city: UK_CITY_CANONICAL_NAMES,
    name_zh: "英国",
    short_name: "UK",
  },
  "United States": {
    icon: `${FLAG_BASE_URL}flag-us.svg`,
    sq_icon: `${config.IMAGE_STATIC_URL}/images/sq-flag-us.svg`,
    city: US_CITY_CANONICAL_NAMES,
    name_zh: "美国",
    short_name: "USA",
  },
  Australia: {
    icon: `${FLAG_BASE_URL}flag-aus.svg`,
    sq_icon: `${config.IMAGE_STATIC_URL}/images/sq-flag-aus.svg`,
    city: AU_CITY_CANONICAL_NAMES,
    name_zh: "澳大利亚",
    short_name: "AUS",
  },
  Ireland: {
    icon: `${FLAG_BASE_URL}flag-ire.svg`,
    sq_icon: `${config.IMAGE_STATIC_URL}/images/sq-flag-ire.svg`,
    city: IR_CITY_CANONICAL_NAMES,
    name_zh: "爱尔兰",
    short_name: "IRE",
  },
  Spain: {
    icon: `${FLAG_BASE_URL}flag-esp.svg`,
    sq_icon: `${config.IMAGE_STATIC_URL}/images/sq-flag-esp.svg`,
    city: SP_CITY_CANONICAL_NAMES,
    name_zh: "西班牙",
    short_name: "ESP",
  },
  Canada: {
    icon: `${FLAG_BASE_URL}flag-can.svg`,
    sq_icon: `${config.IMAGE_STATIC_URL}/images/sq-flag-can.svg`,
    city: CA_CITY_CANONICAL_NAMES,
    name_zh: "加拿大",
    short_name: "CAN",
  },
  Germany: {
    icon: `${FLAG_BASE_URL}flag-ger.svg`,
    sq_icon: `${config.IMAGE_STATIC_URL}/images/sq-flag-ger.svg`,
    city: GER_CITY_CANONICAL_NAMES,
    name_zh: "德国",
    short_name: "GER",
  },
  France: {
    icon: `${config.IMAGE_STATIC_URL}/images/flag-france.svg`,
    sq_icon: `https://static-assets.amberstudent.com/images/fgfr.svg`,

    city: FR_CITY_CANONICAL_NAMES,
    name_zh: "法国",
    short_name: "FR",
  },
  Singapore: {
    icon: `${config.IMAGE_STATIC_URL}/images/flag-singapore.svg`,
    sq_icon: `https://static-assets.amberstudent.com/images/fgsp.svg`,
    city: SG_CITY_CANONICAL_NAMES,
    name_zh: "新加坡",
    short_name: "SG",
  },
  "New Zealand": {
    icon: `${config.IMAGE_STATIC_URL}/images/flag-nz.svg`,
    sq_icon: `https://static-assets.amberstudent.com/images/fgnz.svg`,
    city: NZ_CITY_CANONICAL_NAMES,
    name_zh: "新西兰",
    short_name: "NZ",
  },
};

const CITY_PILLS = [
  { name: "London", name_zh: "伦敦", placeId: "ChIJdd4hrwug2EcRmSrV3Vo6llI" },
  { name: "Glasgow", name_zh: "格拉斯哥", placeId: "ChIJ685WIFYViEgRHlHvBbiD5nE" },
  { name: "Sydney", name_zh: "悉尼", placeId: "ChIJP5iLHkCuEmsRwMwyFmh9AQU" },
  { name: "Liverpool", name_zh: "利物浦", placeId: "ChIJt2BwZIrfekgRAW4XP28E3EI" },
  { name: "Melbourne", name_zh: "墨尔本", placeId: "ChIJ90260rVG1moRkM2MIXVWBAQ" },
  { name: "Manchester", name_zh: "曼彻斯特", placeId: "ChIJ2_UmUkxNekgRqmv-BDgUvtk" },
  { name: "Sheffield", name_zh: "谢菲尔德", placeId: "ChIJFb7o-qkKeUgReLAGr_UnKD4" },
  { name: "Birmingham", name_zh: "伯明翰", placeId: "ChIJc3FBGy2UcEgRmHnurvD-gco" },
];

const SHOW_SEARCH_PROPERTIES = true;

// mweb shows a shortened one-line subtitle; desktop reads the original, longer copy from the
// same key plus a "desktop" suffix. The title works the same way — mweb has its own.
const SUBTITLE_KEY = "home.collectionrailsgroup.trendingproperties.trendingpropertiessubtitle";
const TITLE_KEY = "home.collectionrailsgroup.trendingproperties.trendingpropertiestitle";

const TrendingProperties = ({
  isDesktop = false,
  subSectionPaddingReduced = false,
}: {
  isDesktop?: boolean;
  subSectionPaddingReduced?: boolean;
}) => {
  const { thousandProperties, userLanguage, userCurrency } = useSelector(
    ({ appData }: AppState) => ({
      thousandProperties: appData.thousandProperties,
      userLanguage: appData.userLanguage,
      userCurrency: appData.userCurrency,
    }),
    shallowEqual,
  );
  const [collections, setCollections] = useState(
    // @ts-ignore
    thousandProperties?.["United Kingdom"]?.filter((item: any) => item.city === "London")?.[0]
      ?.inventories || [],
  );

  const fetchData = async (city: any) => {
    let res;
    if (SHOW_SEARCH_PROPERTIES) {
      res = await getNominatedSearchProperties(city.canonicalName, userLanguage, userCurrency);
    } else {
      res = await getTrendingProperties(city.placeId, userLanguage);
    }
    return res;
  };

  const getData = async (city: any) => {
    const res = await fetchData(city);
    // @ts-ignore
    setCollections(res?.result);
  };

  useEffect(() => {
    if (!window) return;
    (async () => {
      try {
        const lsCode = localStorage.getItem("country");
        const res = lsCode ? { country: lsCode } : await getUserCountry();
        const iso = res?.country;
        const entry = iso ? countryCallingCodes[iso as keyof typeof countryCallingCodes] : null;
        const countryName = entry?.name;

        const matchedCountry = Object.keys(COUNTRY_CITY_MAPPING).find(
          (key) => countryName === key || countryName?.includes(key),
        );

        if (matchedCountry && SHOW_SEARCH_PROPERTIES) {
          const firstCity = (COUNTRY_CITY_MAPPING as any)[matchedCountry]?.city?.[0];
          if (firstCity) {
            getData(firstCity);
            return;
          }
        }

        // Fallback: fetch London (UK default)
        getData(SHOW_SEARCH_PROPERTIES ? CITY_CANONICAL_NAMES[0] : CITY_PILLS[0]);
      } catch {
        getData(SHOW_SEARCH_PROPERTIES ? CITY_CANONICAL_NAMES[0] : CITY_PILLS[0]);
      }
    })();
  }, []);
  const { t } = useTranslationHook();

  return (
    <>
      <ThousandPropertiesNewRails
        key="trendingProperties"
        collection={collections}
        title={t(`${TITLE_KEY}${isDesktop ? "desktop" : ""}`)}
        subTitle={t(`${SUBTITLE_KEY}${isDesktop ? "desktop" : ""}`)}
        pills={SHOW_SEARCH_PROPERTIES ? CITY_CANONICAL_NAMES : CITY_PILLS}
        fetchData={fetchData}
        gtagEventAction="clicked_trending_properties_card"
        gtagEventActionPill="clicked_city_thousand_properties"
        subSectionPaddingReduced={subSectionPaddingReduced}
        gtagEventCategory="home_page_trending_properties"
        isDesktop={isDesktop}
        showSeeMoreCard
        id="trendingProperties"
        countryCityMapping={COUNTRY_CITY_MAPPING}
        thousandProperties={thousandProperties}
      />
    </>
  );
};

export default wrapperHOC(TrendingProperties, {
  componentName: "TrendingProperties-Home",
  showForChina: true,
  showForMiniProgram: false,
});
